import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ScrollStory } from "@/components/site/ScrollStory";

const IMG = "https://static.prod-images.emergentagent.com/jobs/55e8a073-484f-45a0-9ce0-724193ee87a9/images/";

const ORIGINS = [
  { name: "Mysore", state: "Karnataka", img: "0dce0d2fb9734988389c078574fe1290216110c699e21dbda12a7d711d19b78f.jpeg" },
  { name: "Banaras", state: "Uttar Pradesh", img: "1ce0404c91c3e6df389cebfd516af16a111b0c8e44e597764487d3f14d34ea9f.jpeg" },
  { name: "Kanchipuram", state: "Tamil Nadu", img: "dabcce0a80c1d3568f7a1a5d33fa8dc03213dd36f478d74a789b81e85f9680ce.jpeg" },
  { name: "Paithan", state: "Maharashtra", img: "2e698f8fbc956c68b5c9b055dd5534ffee4870ebaad97f0b0476791a7bc7a0c5.jpeg" },
  { name: "Bishnupur", state: "West Bengal", img: "7a421613856a290dd64868c65af7422c5c1549d63478232c407ead17cd0b2e06.jpeg" },
  { name: "Kerala", state: "Kerala", img: "3d81d06734a58350ca14e16934c90d3957d28eb2d7d2ae266b890f973a85527f.jpeg" },
];

const INFO = [
  { k: "01", t: "What we do", d: "We bring handwoven silk sarees from India's great weaving towns into one place - Mysore crepe, Banarasi brocade, Kanjeevaram temple borders, Paithani peacocks and more - sourced directly from the looms that made them famous." },
  { k: "02", t: "About us", d: "[placeholder: a short paragraph about the founders, the team and the weaver families Ancient Origin works with.]" },
  { k: "03", t: "Who it's for", d: "For brides and their mothers, for collectors of craft, for the diaspora longing for home, and for anyone who believes a saree should carry a story - not just a pattern." },
  { k: "04", t: "Our history", d: "[placeholder: how Ancient Origin started - the first loom visited, the first saree sold, and the idea of connecting every corner of India through cloth.]" },
];

function Info() {
  return (
    <section id="about" className="mx-auto max-w-6xl px-6 py-28" data-testid="info-section">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4 md:sticky md:top-28 self-start">
          <p className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37]">Our story</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-4 leading-tight">One nation.<br /><span className="font-serif-it text-[#E06D3B] normal-case">a thousand looms.</span></h2>
        </div>
        <div className="md:col-span-8 space-y-14">
          {INFO.map((x, i) => (
            <motion.div key={x.k} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: i * 0.05 }} className="border-t border-[#D4AF37]/15 pt-8 grid sm:grid-cols-[80px_1fr] gap-4" data-testid={`info-${x.k}`}>
              <span className="font-display text-[#D4AF37]/60">{x.k}</span>
              <div>
                <h3 className="font-display text-xl tracking-wide">{x.t}</h3>
                <p className="mt-3 text-[#A89F91] leading-relaxed">{x.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Origins() {
  return (
    <section id="origins" className="py-24" data-testid="origins-strip">
      <div className="mx-auto max-w-6xl px-6 flex items-end justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37]">The origins</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-4">From Mysore to <span className="font-serif-it text-[#E06D3B]">Banaras</span></h2>
        </div>
        <Link to="/store" className="hidden sm:inline text-xs uppercase tracking-[0.3em] text-[#A89F91] hover:text-[#F3E5AB]">All sarees →</Link>
      </div>
      <div className="mt-12 flex gap-5 overflow-x-auto scrollbar-thin px-6 pb-4 snap-x">
        {ORIGINS.map((o) => (
          <Link key={o.name} to={`/store?origin=${o.name}`} className="group relative shrink-0 snap-start w-60 sm:w-72 overflow-hidden rounded-sm" data-testid={`origin-${o.name.toLowerCase()}`}>
            <img src={IMG + o.img} alt={o.name} className="aspect-[3/4] w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0907] via-[#0B0907]/20 to-transparent" />
            <div className="absolute bottom-5 left-5">
              <p className="font-display text-2xl">{o.name}</p>
              <p className="font-serif-it text-[#A89F91]">{o.state}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function StoryIntro({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      key="story-intro"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
      className="fixed inset-0 z-[60] bg-[#0B0907] flex flex-col items-center justify-center text-center px-6"
      data-testid="story-intro"
    >
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="font-display italic text-4xl sm:text-6xl lg:text-7xl text-[#F5EFE6] leading-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]"
      >
        Start the story?
      </motion.p>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 2.2, delay: 0.8, ease: "linear" }}
        className="mt-10 h-[1px] w-48 bg-[#D4AF37]/50 origin-left"
      />
    </motion.div>
  );
}

export default function Landing() {
  const [showIntro, setShowIntro] = useState(true);

  return (
    <div className="bg-[#0B0907]" data-testid="landing-page">
      <AnimatePresence>{showIntro && <StoryIntro onDone={() => setShowIntro(false)} />}</AnimatePresence>
      <Header />
      <ScrollStory />
      <div className="zari-divider mx-auto max-w-4xl" />
      <Info />
      <div className="zari-divider mx-auto max-w-4xl" />
      <Origins />
      <section className="mx-auto max-w-6xl px-6 py-20 text-center">
        <p className="font-serif-it text-3xl sm:text-4xl text-[#F5EFE6]">Every thread carries a village.</p>
        <Link to="/store" className="btn-glow mt-8 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/50 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-[#F3E5AB] hover:bg-[#D4AF37] hover:text-[#0B0907]" data-testid="explore-store-button-bottom">
          Explore our store <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
      <Footer />
    </div>
  );
}
